import cond from 'lodash/fp/cond';
import React from 'react';
import invariant from 'tiny-invariant';
import {
  AfterPageProps,
  createApiClient,
  createInitialPropsRedirectToLogin,
  makeAfterComponent,
  wrapLayout,
} from '../../@sprinx/react-after-razzle';
import { ArticleDetail } from '../../api/articles/Article';
import { getArticleDetail } from '../../api/articles/articleDetail';
import commonThingsToLoad from '../../api/commonThingsToLoad';
import ErrorInternal from '../../components/ErrorInternal';
import { routeUrl } from '../../routesBuilder';
import ArticleDetailPageLayout from './ArticleDetailPageLayout';

export interface ArticleDetailPageProps extends AfterPageProps {
  articleDetail?: ArticleDetail;
}

const ArticleDetailPage: React.FC<ArticleDetailPageProps> = ({ articleDetail, error }) => {
  if (error) return <ErrorInternal error={error} />;

  invariant(articleDetail, "ArticleDetailPage requires 'articleDetail' to be defined.");

  return <ArticleDetailPageLayout articleDetail={articleDetail} />;
};

ArticleDetailPage.displayName = 'ArticleDetailPage';

export default makeAfterComponent<
  { articleDetail?: ArticleDetail; error?: any },
  ArticleDetailPageProps,
  { id?: string }
>(wrapLayout(ArticleDetailPage), async ({ match, initialAppProps, getAppProps }) => {
  try {
    const appProps = getAppProps ? getAppProps() : initialAppProps;
    const apiClient = createApiClient({ accessToken: appProps.auth?.accessToken });
    const locale = appProps.locale;

    // Convert URLs to article IDs...
    const articleId = cond<void, string | undefined>([
      [
        (): boolean => match.url === routeUrl('technicalAdvisor', { locale, params: undefined }),
        (): string => 'Hlavicka-Technicky-radce',
      ],
      [
        (): boolean => match.url === routeUrl('termsAndConditions', { locale, params: undefined }),
        (): string => 'Hlavicka-Obchodni-podminky',
      ],
      [
        (): boolean => match.url === routeUrl('ourServices', { locale, params: undefined }),
        (): string => 'HomePage-Servisni-sluzby',
      ],
      [
        (): boolean => match.url === routeUrl('tireSelection', { locale, params: undefined }),
        (): string => 'HomePage-Vyber-pneumatiky',
      ],
      [
        (): boolean => match.url === routeUrl('tiresDictionary', { locale, params: {} }),
        (): string => 'Paticka-Slovnik-o-pneumatikach',
      ],
      [(): boolean => match.url === routeUrl('siteMap', { locale, params: undefined }), (): string => 'mapa-stranek'],
      [() => true, () => match.params.id],
    ])();

    if (!articleId) {
      throw new Error('Invalid article id');
    }

    if (appProps) {
      const [articleDetail, ...commonThings] = await Promise.all([
        getArticleDetail(apiClient, locale, articleId),
        ...commonThingsToLoad.promises({ apiClient }),
      ]);

      appProps.propagateToState({
        ...commonThingsToLoad.toRegister(commonThings),
      });

      return { articleDetail };
    }

    const articleDetail = await getArticleDetail(apiClient, locale, articleId);
    return { articleDetail };
  } catch (error) {
    if (error.response?.status === 401) {
      return createInitialPropsRedirectToLogin({ match });
    }
    return { error };
  }
});
